import React from "react";
import { ECalendarTlCode } from "./ECalendarTlCode";
import {
  DayInfos,
  DaySlices,
  getDates,
  getInterval,
  MILLI,
  sliceDay,
  SliceInfo,
} from "./utils/dates";
import {
  add,
  endOfDay,
  isWithinInterval,
  Locale,
  startOfDay,
  startOfWeek,
} from "date-fns";
import {
  atom,
  selectorFamily,
  useRecoilCallback,
  useSetRecoilState,
} from "recoil";
import { differenceInMinutes } from "date-fns/esm";
import {EDays} from "./EDays";

export type CalendarViewMode = "week" | "day" | "month";

export type TimeSelection = {
  start: Date;
  end: Date;
};

export type HourLimit = {
  startHour: number;
  endHour: number
}
const CalendarContext = React.createContext<ICalendarContext>(null);

export interface CalendarEvent<T> {
  obj: T;
  internalId: number;
  startDate: Date;
  endDate: Date;
}

export interface HorizontalResourceProps {
  keys: any[];
  checkEventByKey: (key: any, event: CalendarEvent<any>) => boolean;
  renderResourceKey: (key: any) => React.ReactElement;
  keyColumnWidthInPx: number;
  resourceRowHeightInPx: number;
}


export interface CalendarContextProviderProps {
  children: React.ReactNode;
  translateFn?: (code: ECalendarTlCode) => string;
  sliceHeightInPx?: number;
  keyColumnWidthInPx?: number;
  daySliceInMinutes?: DaySlices;
  dateFNSLocale?: Locale;
  mode?: CalendarViewMode;
  initialEvents?: any[];
  getEventDates: (item: any) => Interval;
  renderEvent: (e: any) => React.ReactElement;
  renderHourHeader?: (e: number, height: number) => React.ReactElement;
  renderDaysHeader?: (
    e: Date,
    isToday: boolean,
    isWeekend: boolean
  ) => React.ReactElement;
  renderEmptyHeader?: (currentWidth: number) => React.ReactElement;
  startDate?: Date;
  availableViewModes?: CalendarViewMode[];
  horizontalResources?: HorizontalResourceProps;
  hideDays?: EDays[];
  scrollToHour?: number;
  createEvent?: (Date1: Date, Date2: Date) => void;
  onDateChanged?: (StartDate: Date, EndDate: Date) => void;
  isWholeDay: (startHour: number, endHour: number) => boolean;
  hourLimit? : HourLimit;
}

export const calendarAtoms = {
  hoveredSlice: atom<{ bound1: Date; bound2: Date }>({
    key: "hoveredSlice",
    default: null,
  }),
};

export const calendarSelectors = {
  isSliceSelected: selectorFamily({
    key: "isSelected",
    get:
      (sliceDate: Date) =>
      ({ get }) => {
        const hoveredCell = get(calendarAtoms.hoveredSlice);
        if (!hoveredCell) return false;
        const interval = getInterval(hoveredCell.bound1, hoveredCell.bound2);
        return isWithinInterval(
          add(sliceDate, {
            seconds: 1,
          }),
          interval
        );
      },
  }),
};

export const CalendarContextProvider: React.FC<
  CalendarContextProviderProps
> = ({
  children,
  translateFn,
  daySliceInMinutes,
  sliceHeightInPx,
  keyColumnWidthInPx,
  dateFNSLocale,
  initialEvents,
  startDate,
  mode = "week",
  horizontalResources,
  renderEvent,
  getEventDates,
  availableViewModes = ["day", "week", "month"],
  hideDays,
  scrollToHour,
  renderHourHeader,
  renderDaysHeader,
  renderEmptyHeader,
  createEvent,
  onDateChanged,
  isWholeDay,
  hourLimit
}) => {
  const [viewMode, setViewMode] = React.useState<CalendarViewMode>(mode);
  const [contentOverflow, setContentOverflow] = React.useState(false);

  const ids = React.useRef(1);

  const [events, setEvents] = React.useState([]);

  const changeCalendarViewMode = React.useCallback(
    (viewMode: CalendarViewMode) => {
      setViewMode(viewMode);
    },
    []
  );

  const [currentDate, setCurrentDate] = React.useState(new Date());

  const days = React.useMemo(() => {
    switch (viewMode) {
      case "week":
        return getDates(currentDate, 7, hideDays);
      case "day":
        return getDates(currentDate, 1, hideDays);
      case "month":
        return getDates(currentDate, 31, hideDays);
      default:
        return getDates(currentDate, 1, hideDays);
    }
  }, [currentDate, hideDays, viewMode]);

  const getDayEvents = React.useCallback(
    (day: DayInfos) => {
      return events.filter(
        (e) =>
          isWithinInterval(e.startDate, {
            start: startOfDay(day.date),
            end: endOfDay(day.date),
          }) ||
          isWithinInterval(e.endDate, {
            start: startOfDay(day.date),
            end: endOfDay(day.date),
          })
      );
    },
    [events]
  );

  React.useEffect(() => {
    setCurrentDate(
      startOfWeek(startDate || new Date(), {
        locale: dateFNSLocale,
      })
    );
  }, [dateFNSLocale, setCurrentDate, startDate]);

  React.useEffect(() => {
    if (initialEvents) {
      setEvents(
        initialEvents.map((e) => {
          const dates = getEventDates(e);
          return {
            obj: e,
            internalId: ids.current++,
            startDate: dates.start,
            endDate: dates.end,
          } as CalendarEvent<any>;
        })
      );
    }
  }, [getEventDates, initialEvents, setEvents]);

  const innerSetContentOverflow = React.useCallback((isOverFlow: boolean) => {
    setContentOverflow(isOverFlow);
  }, []);
  const moveEvent = React.useCallback(
    (eventId: number, startDate: Date, endDate?: Date) => {
      const event = events.find((d) => d.internalId === eventId);

      const diff = Math.abs(
        differenceInMinutes(event.startDate, event.endDate)
      );

      const newEvent = {
        ...event,
        startDate,
        endDate: add(startDate, { minutes: diff }),
      };

      const excludes = events.filter(
        (d) => d.internalId !== newEvent.internalId
      );
      const next = [...excludes, newEvent];

      setEvents(next);
    },
    [events, setEvents]
  );

  const daySliceLengthInMs = React.useMemo(() => {
    return MILLI.minutes * daySliceInMinutes;
  }, [daySliceInMinutes]);

  const dayTimeSlices = React.useMemo(() => {
    return sliceDay(daySliceLengthInMs);
  }, [daySliceLengthInMs]);

  const navigateInDays = React.useCallback(
    (daysToAddOrRemove: number) => {
      var newDate = add(currentDate, { days: daysToAddOrRemove });
      setCurrentDate(newDate);
      onDateChanged(
        newDate,
        add(newDate, { days: Math.abs(daysToAddOrRemove) })
      );
    },
    [currentDate, onDateChanged]
  );

  const setSelection = useSetRecoilState(calendarAtoms.hoveredSlice);

  const getSelection = useRecoilCallback(({ snapshot }) => async () => {
    return await snapshot.getPromise(calendarAtoms.hoveredSlice);
  });

  const onCreateSelection = React.useCallback(async () => {
    const selection = await getSelection();
    if (selection) {
      if (!!createEvent) {
        createEvent(selection.bound1, selection.bound2);
      }
      setSelection(null);
    }
  }, [createEvent, getSelection, setSelection]);

  const finalKeyColumnWidth = React.useMemo(() => {
    return horizontalResources?.keyColumnWidthInPx || keyColumnWidthInPx;
  }, [horizontalResources?.keyColumnWidthInPx, keyColumnWidthInPx]);

  return (
    <CalendarContext.Provider
      value={{
        dateFNSLocale,
        daySliceInMinutes,
        sliceHeightInPx,
        keyColumnWidthInPx: finalKeyColumnWidth,
        translateFn,
        changeCalendarViewMode,
        viewMode,
        contentOverflow,
        setContentOverflow: innerSetContentOverflow,
        currentDate,
        moveEvent,
        getDayEvents,
        dayTimeSlices,
        daySliceLengthInMs,
        days,
        navigateInDays,
        availableViewModes,
        onCreateSelection,
        horizontalResources,
        getEventDates,
        renderEvent,
        renderHourHeader,
        renderDaysHeader,
        scrollToHour,
        renderEmptyHeader,
        isWholeDay,
        hourLimit
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
};

interface ICalendarContext
  extends Omit<CalendarContextProviderProps, "children"> {
  changeCalendarViewMode: (viewMode: CalendarViewMode) => void;
  viewMode: CalendarViewMode;
  currentDate: Date;
  contentOverflow: boolean;
  setContentOverflow: (isOverFlow: boolean) => void;
  getDayEvents: (day: DayInfos) => CalendarEvent<any>[];
  moveEvent: (eventId: number, startDate: Date, endDate?: Date) => void;
  dayTimeSlices: SliceInfo[];
  daySliceLengthInMs: number;
  days: DayInfos[];
  navigateInDays: (daysToAddOrRemove: number) => void;
  availableViewModes: CalendarViewMode[];
  onCreateSelection: () => void;
  isWholeDay: (startHour: number, endHour: number) => boolean;
  hourLimit: HourLimit
}

export const useCalendarContext = () => React.useContext(CalendarContext);
